import React, { useContext, useState } from 'react'
import LinkifiedText from './LinkifiedText'
import { observer } from 'mobx-react-lite'
import { PencilSquare, StopCircle } from 'react-bootstrap-icons'
import { CampaignContext } from '../../contexts/CampaignContext'
import { TimelineContext } from '../../contexts/TimelineContext'
import { useReadOnly } from '../../contexts/ReadOnlyContext'
import { collectTimelineNotes } from '../../utils/collectTimelineNotes'
import { formatTimelineDateForInput } from '../../utils/timelineAssetDates'
import EditAssetModal from '../ProjectManager/Campaign/CampaignPageParts/TimelineParts/TimelineSidebarParts/EditAssetModal'
import {
  actionLabelFor,
  actionPrimaryLabelFor,
  actionProviderFor,
  providerAccentColors,
} from './TimelineParts/timelineActionDisplay'

const DEFAULT_ACCENT_COLOR = '#50d47f'
const PROJECT_NOTE_KIND = 'project'

function isProjectNote(note) {
  return note.kind === PROJECT_NOTE_KIND
}

function timelineNoteAccentColor(note) {
  if (isProjectNote(note)) return note.sourceAsset?.theme_color || DEFAULT_ACCENT_COLOR

  const provider = actionProviderFor(note.sourceAsset)
  return providerAccentColors[provider] || DEFAULT_ACCENT_COLOR
}

function timelineNoteTitleParts(note) {
  const asset = note.sourceAsset
  if (isProjectNote(note)) return { primaryLabel: note.title, actionLabel: '', fullLabel: note.title }

  const actionLabel = actionLabelFor(asset)
  const primaryLabel = actionPrimaryLabelFor(asset)
  const fullLabel = actionLabel ? `${primaryLabel} - ${actionLabel}` : primaryLabel

  return { primaryLabel, actionLabel, fullLabel }
}

function TimelineNoteAssetTitle({ note }) {
  const accentColor = timelineNoteAccentColor(note)
  const { primaryLabel, actionLabel, fullLabel } = timelineNoteTitleParts(note)

  return (
    <div className='flex min-w-0 items-center gap-2 pr-8'>
      <StopCircle style={{ color: accentColor }} className='shrink-0 text-lg' />
      <h3 className='min-w-0 flex-1 truncate text-sm font-semibold leading-snug text-rb-black dark:text-white' title={fullLabel}>
        <span>{primaryLabel}</span>
        {actionLabel && (
          <>
            <span className='text-rb-lighter-grey dark:text-gray-400'> - </span>
            <span>{actionLabel}</span>
          </>
        )}
      </h3>
    </div>
  )
}

function TimelineNoteEditAction({ note }) {
  const { timelineState, campaignScenario } = useContext(TimelineContext)
  const { campaign } = useContext(CampaignContext)
  const [editModalOpen, setEditModalOpen] = useState(false)

  const asset = note.sourceAsset
  const campaignStartDate = formatTimelineDateForInput(campaign?.start_date)
  const campaignEndDate = formatTimelineDateForInput(campaign?.end_date)

  const handleEditSave = async ({ startDate, endDate, note: nextNoteBody }) => {
    const nextStartDate = startDate || formatTimelineDateForInput(asset?.start_date)
    const nextEndDate = endDate || formatTimelineDateForInput(asset?.end_date)

    await timelineState.updateTimelineItemDate(
      asset,
      asset.start_date,
      nextStartDate,
      nextEndDate,
      campaignScenario,
      nextNoteBody,
    )
  }

  return (
    <>
      <button
        type='button'
        onClick={() => setEditModalOpen(true)}
        className='absolute right-3 top-3 rounded p-1 text-rb-black hover:bg-gray-100 dark:text-white dark:hover:bg-rb-dark-mode'
        aria-label={`Edit ${note.title}`}
      >
        <PencilSquare className='text-sm' />
      </button>
      <EditAssetModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        asset={asset}
        initialStartDate={formatTimelineDateForInput(asset?.start_date)}
        initialEndDate={formatTimelineDateForInput(asset?.end_date)}
        initialNote={note.body}
        initialName={asset?.name}
        campaignStartDate={campaignStartDate}
        campaignEndDate={campaignEndDate}
        onSave={handleEditSave}
      />
    </>
  )
}

function TimelineNoteCard({ note, canEdit }) {
  return (
    <li className='relative mb-2 rounded-xl border-1 border-rb-widget-border bg-white px-3 py-3 shadow-sm dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode-lighter'>
      <div className='flex flex-col gap-2'>
        <TimelineNoteAssetTitle note={note} />
        <p className='text-xs italic leading-relaxed text-rb-mat-grey dark:text-gray-200'><LinkifiedText text={note.body} /></p>
      </div>
      {canEdit && <TimelineNoteEditAction note={note} />}
    </li>
  )
}

export default observer(function TimelineNotesSection() {
  const { campaignScenario, readonly } = useContext(TimelineContext)
  const archived = useReadOnly()
  const notes = collectTimelineNotes(campaignScenario)
  const canEditNotes = !readonly && !archived

  return (
    <div className='mt-5 w-full'>
      <div className='flex items-center justify-between'>
        <h2 className='font-bold text-xl dark:text-white'>Notes</h2>
        <span className='text-rb-lighter-grey dark:text-gray-400'>{notes.length}</span>
      </div>

      {notes.length === 0 ? (
        <p className='mt-3 text-sm text-rb-lighter-grey dark:text-gray-400'>No notes yet.</p>
      ) : (
        <ul className='mt-2 space-y-2 list-none p-0 m-0'>
          {notes.map((note) => (
            <TimelineNoteCard
              key={note.key}
              note={note}
              canEdit={canEditNotes}
            />
          ))}
        </ul>
      )}
    </div>
  )
})
